import { useCallback, useState } from "react";
import {
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentType,
  OrganizationTalentIndicatorFragment,
  TalentIndicatorConfigInput,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Input from "@components/input/input";
import Sidebar from "@components/sidebar/sidebar";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import TalentIndicatorSidebarIndicator from "./talent-indicator-sidebar-indicator";

export type TalentIndicatorForm = {
  talentIndicatorId: number;
  title: string;
  description: string;
  enabled: boolean;
  responses: AssessmentQuestionResponses;
  responseVisibility: AssessmentQuestionResponseVisibility;
  comment: AssessmentQuestionComment;
  isManagerIndicator: boolean;
};

const setupTalentIndicatorForm = (
  talentIndicators: OrganizationTalentIndicatorFragment[],
  talentIndicatorSetup: TalentIndicatorConfigInput
) => {
  const form: TalentIndicatorForm[] = [
    ...talentIndicatorSetup.talentIndicators.map((indicatorSetup) => {
      const data = assertNonNull(
        talentIndicators.find(
          (talentIndicator) =>
            talentIndicator.id ===
            assertNonNull(indicatorSetup?.talentIndicatorId)
        )
      );
      return {
        talentIndicatorId: data.id,
        title:
          data.questionTitle && data.questionTitle.trim() !== ""
            ? data.questionTitle
            : data.customName && data.customName.trim() !== ""
            ? data.customName
            : data.name,
        description: indicatorSetup?.description ?? data.description,
        enabled: true,
        responses: assertNonNull(indicatorSetup?.responses),
        responseVisibility: assertNonNull(indicatorSetup?.responseVisibility),
        comment: assertNonNull(indicatorSetup?.comment),
        isManagerIndicator: data.isManagerIndicator,
      };
    }),
    ...talentIndicators
      .filter(
        (talentIndicator) =>
          !talentIndicatorSetup.talentIndicators
            .map((setup) => setup?.talentIndicatorId)
            .includes(talentIndicator.id)
      )
      .map((talentIndicator) => {
        return {
          talentIndicatorId: talentIndicator.id,
          title:
            talentIndicator.questionTitle &&
            talentIndicator.questionTitle.trim() !== ""
              ? talentIndicator.questionTitle
              : talentIndicator.customName &&
                talentIndicator.customName.trim() !== ""
              ? talentIndicator.customName
              : talentIndicator.name,
          description: talentIndicator.description,
          enabled: false,
          responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
          responseVisibility:
            AssessmentQuestionResponseVisibility.HiddenFromSubject,
          comment: AssessmentQuestionComment.Optional,
          isManagerIndicator: talentIndicator.isManagerIndicator,
        };
      }),
  ];

  return form;
};

const TalentIndicatorSidebar = ({
  onClose,
  canUpdate,
  assessmentType,
  talentIndicatorData,
  talentIndicatorSetup,
  onUpdateTalentIndicators,
}: {
  onClose: () => void;
  canUpdate: boolean;
  assessmentType: AssessmentType;
  talentIndicatorData: OrganizationTalentIndicatorFragment[];
  talentIndicatorSetup: TalentIndicatorConfigInput;
  onUpdateTalentIndicators: (data: TalentIndicatorConfigInput) => void;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();

  const [proposedTitle, setProposedTitle] = useState(
    talentIndicatorSetup.title
  );
  const [proposedDescription, setProposedDescription] = useState(
    talentIndicatorSetup.description
  );
  const [proposedTalentIndicators, setProposedTalentIndicators] = useState<
    TalentIndicatorForm[]
  >(setupTalentIndicatorForm(talentIndicatorData, talentIndicatorSetup));

  const handleSave = useCallback(() => {
    onUpdateTalentIndicators({
      title: proposedTitle,
      description: proposedDescription,
      talentIndicators: proposedTalentIndicators
        .filter((indicator) => indicator.enabled)
        .map((indicator) => ({
          talentIndicatorId: indicator.talentIndicatorId,
          responses: indicator.responses,
          responseVisibility: indicator.responseVisibility,
          comment: indicator.comment,
          description: indicator.description,
        })),
    });
    onClose();
  }, [
    onUpdateTalentIndicators,
    proposedTitle,
    proposedDescription,
    proposedTalentIndicators,
    onClose,
  ]);

  const handleMoveIndicator = useCallback(
    (index: number, direction: "up" | "down") => {
      const newIndicators = [...proposedTalentIndicators];
      const toMove = newIndicators.splice(index, 1)[0];
      const toIndex = direction === "up" ? index - 1 : index + 1;
      newIndicators.splice(toIndex, 0, toMove);
      setProposedTalentIndicators(newIndicators);
    },
    [proposedTalentIndicators]
  );

  return (
    <Sidebar
      wide
      title={`${
        assessmentType === AssessmentType.Performance ? "Employee" : "Manager"
      } Talent Indicators`}
      className="bg-gray-50"
      headerClassName="bg-gray-50"
      onClose={onClose}
      additionalActions={
        <>
          <Button theme={buttonTheme.text} text="Discard" onClick={onClose} />
          <Button theme="primary" text="Save" onClick={handleSave} />
        </>
      }
    >
      <div className="mx-6 p-4 bg-gray-100 rounded-md flex gap-4 text-gray-800 text-sm">
        <TopicflowIcon type="info" className="h-5 w-5 text-gray-600" />
        <div>
          <div className="font-semibold mb-1">
            Only certain details of talent indicators can be modified here
          </div>
          {isAdmin ? (
            <div>
              To edit the details of each indicator, you will need to go to the
              "Talent Indicators" page within your{" "}
              {label("organization", { capitalize: true })} Settings.
            </div>
          ) : (
            <div>
              To edit the details of each indicator, you will need to contact
              your administrator.
            </div>
          )}
          {isAdmin && (
            <Button
              className="mt-4"
              to={`/settings/organization/${organization.id}/talent-indicators`}
              target="_blank"
              text="View Talent Indicators"
            />
          )}
        </div>
      </div>

      <div className="px-6 mt-8">
        <Input
          disabled={!canUpdate}
          aria-label="Section input"
          className="w-full"
          placeholder="Add header"
          value={proposedTitle ?? ""}
          onChange={(e) => {
            setProposedTitle(e.target.value);
          }}
        />
        <TextareaWysiwyg
          editable={canUpdate}
          className={classNames("mt-4", canUpdate && "bg-white")}
          placeholder="Add decription (optional)"
          value={proposedDescription}
          deps={[talentIndicatorData]}
          onChangeValue={setProposedDescription}
        />
      </div>

      <div className="p-6 flex flex-col gap-6">
        {proposedTalentIndicators.map((talentIndicator, index) => {
          return (
            <TalentIndicatorSidebarIndicator
              key={talentIndicator.talentIndicatorId}
              sectionIndex={0}
              questionIndex={index}
              isFirstQuestion={index === 0}
              isLastQuestion={index === proposedTalentIndicators.length - 1}
              talentIndicator={talentIndicator}
              canUpdate={canUpdate}
              onUpdateQuestion={(data) => {
                setProposedTalentIndicators(
                  proposedTalentIndicators.map((indicator) => {
                    if (
                      indicator.talentIndicatorId === data.talentIndicatorId
                    ) {
                      return data;
                    }
                    return indicator;
                  })
                );
              }}
              onMoveQuestionUp={() => {
                handleMoveIndicator(index, "up");
              }}
              onMoveQuestionDown={() => {
                handleMoveIndicator(index, "down");
              }}
            />
          );
        })}
      </div>
    </Sidebar>
  );
};

export default TalentIndicatorSidebar;
